import { PxtraApiClientLegacy } from '@pxtra-client-lib-legacy';
import {
  CompanyExtended,
  CompanyBenefitSettings,
  CompanyContactData,
  CompanyGeneralData,
  CompanyPaymentData,
  CompanySettings,
  CostCenter,
  CostCenterRequest,
  Department,
  DepartmentRequest,
  Location,
  LocationRequest,
  Group,
  GroupRequest,
  PatchedCompanyContactDataRequest,
  PatchedCompanyGeneralDataRequest,
  PatchedCompanyPaymentDataRequest,
  PatchedCompanySettingsRequest,
  PatchedCostCenterRequest,
  PatchedDepartmentRequest,
  PatchedLocationRequest,
  PatchedGroupRequest,
  PayrollExportMappingRequest,
  CompanyCustomBenefit,
  CompanyCustomBenefitRequest,
  CompanyCustomBenefitPatchRequest,
  UserCompany,
  PaychexAPICredentials,
  BenefitEnum,
  BenefitSet,
  PatchedCompanyJobbikeSettingsPatchRequest
} from '@pxtra-client-lib-legacy/gen';
import { makeAutoObservable, runInAction, when } from 'mobx';
import Util from '../misc/util';
import AuthenticationStore from './authenticationStore';
import { showKomboConnect } from '@kombo-api/connect';

export interface PayrollExportMapping {
  salaryName: string;
  externalId?: string;
}

const CURRENT_COMPANY_STORAGE_KEY = 'CURRENT_COMPANY';

class CompanyStore {
  private apiClient: PxtraApiClientLegacy;

  private _managedCompanies?: Array<UserCompany>;
  private _company?: CompanyExtended;
  private _benefitSettings?: CompanyBenefitSettings;
  private _generalData?: CompanyGeneralData;
  private _contactData?: CompanyContactData;
  private _locations?: Array<Location>;
  private _departments?: Array<Department>;
  private _costCenters?: Array<CostCenter>;
  private _groups?: Array<Group>;
  private _paymentData?: CompanyPaymentData;
  private _settings?: CompanySettings;
  private _payrollExportMapping?: PayrollExportMapping[];
  private _customBenefitSettings?: CompanyCustomBenefit[];
  private _hrisSyncState: string | null = null;
  private _hrisSyncDate: string | null = null;
  private _paychexApiCredentials?: PaychexAPICredentials | null = null;
  private _bookedBenefits: BenefitEnum[] = [];
  private _benefitSets?: BenefitSet[];

  constructor(
    apiClient: PxtraApiClientLegacy,
    private authenticationStore: AuthenticationStore
  ) {
    this.apiClient = apiClient;
    this.apiClient.isLoggedIn.subscribe(async (value) => {
      if (value) {
        await this.fetchManagedCompanies();
        const storedCurrentCompany = localStorage.getItem(CURRENT_COMPANY_STORAGE_KEY) ?? '';

        if (this.managedCompanies) {
          const companyIds = this.managedCompanies.map((c) => c.id);
          let index = companyIds.indexOf(storedCurrentCompany);
          index = index <= 0 ? 0 : index;
          const company = await this.apiClient.companies.companiesRetrieve({
            id: companyIds[index]
          });
          await this.setCurrentCompany(company);
        }
      }
    });
    makeAutoObservable(this);
    when(
      () => !this.authenticationStore.isLoggedIn,
      () => {
        this._clear();
      }
    );
  }

  private _clear() {
    this._managedCompanies = undefined;
    this._company = undefined;
    this._benefitSettings = undefined;
    this._generalData = undefined;
    this._contactData = undefined;
    this._locations = undefined;
    this._departments = undefined;
    this._costCenters = undefined;
    this._groups = undefined;
    this._paymentData = undefined;
    this._settings = undefined;
    this._payrollExportMapping = undefined;
    this._customBenefitSettings = undefined;
    this._hrisSyncState = null;
    this._hrisSyncDate = null;
    this._paychexApiCredentials = null;
    this._bookedBenefits = [];
    this._benefitSets = [];
  }

  private async _fetchCompany() {
    if (!this.company) return;
    const res = await this.apiClient.companies.companiesRetrieve({ id: this.company.id });
    runInAction(() => (this._company = res));
  }
  private async _fetchGeneralData() {
    if (!this.company) return;
    const res = await this.apiClient.companies.companiesGeneralDataRetrieve({
      id: this.company.id
    });
    runInAction(() => (this._generalData = res));
  }

  private async _fetchContactData() {
    if (!this.company) return;
    const res = await this.apiClient.companies.companiesContactDataRetrieve({
      id: this.company.id
    });
    runInAction(() => (this._contactData = res));
  }

  private async _fetchSettings() {
    if (!this.company) return;
    const res = await this.apiClient.companies.companiesSettingsRetrieve({ id: this.company.id });
    runInAction(() => (this._settings = res));
  }

  public fetchCompanySettings() {
    return Promise.all([this._fetchGeneralData(), this._fetchContactData(), this._fetchSettings()]);
  }

  public async fetchBenefitSettings() {
    if (!this.company) {
      await this._fetchCompany();
    }

    if (this.company) {
      const res = await this.apiClient.companies.companiesBenefitSettingsRetrieve({
        id: this.company.id
      });
      runInAction(() => (this._benefitSettings = res));
    }
  }

  public async fetchPaymentData() {
    if (!this.company) return;
    const res = await this.apiClient.companies.companiesPaymentDataRetrieve({
      id: this.company.id
    });
    runInAction(() => (this._paymentData = res));
  }

  public async fetchLocations() {
    if (!this._company) await this._fetchCompany();
    if (this._company?.id) {
      const res = await this.apiClient.locations.companiesLocationsList({
        parentCompany: this._company.id
      });

      runInAction(() => (this._locations = res.results));
    }
  }

  public async fetchDepartments() {
    if (!this._company) await this._fetchCompany();
    if (this._company?.id) {
      const res = await this.apiClient.departments.companiesDepartmentsList({
        parentCompany: this._company.id
      });

      runInAction(() => (this._departments = res.results));
    }
  }

  public async fetchCostCenters() {
    if (!this._company) await this._fetchCompany();
    if (this._company?.id) {
      const res = await this.apiClient.costCenters.companiesCostCentersList({
        parentCompany: this._company.id
      });

      runInAction(() => (this._costCenters = res.results));
    }
  }

  public async fetchGroups() {
    if (!this._company) await this._fetchCompany();
    if (this._company?.id) {
      const res = await this.apiClient.groups.companiesGroupsList({
        parentCompany: this._company.id
      });

      runInAction(() => (this._groups = res.results));
    }
  }

  public async fetchManagedCompanies() {
    const res = await this.apiClient.user.userCompaniesRetrieve();

    runInAction(() => {
      this._managedCompanies = res.managed_companies.map((c) => ({
        id: c.id,
        name: c.name,
        status: c.status
      }));
    });
  }

  public async fetchCustomBenefitSettings() {
    if (!this.company) await this._fetchCompany();
    if (this.company?.id) {
      const res = await this.apiClient.benefits.benefitsCustomList({
        company: this.company.id
      });

      runInAction(() => (this._customBenefitSettings = res.results));
    }
  }

  public async fetchBenefitSets() {
    if (!this.company) await this._fetchCompany();
    if (this.company?.id) {
      const res = await this.apiClient.benefits.benefitsBenefitSetsList({
        company: this.company.id
      });

      runInAction(() => (this._benefitSets = res.results));
    }
  }

  public get company() {
    return this._company;
  }

  public get managedCompanies() {
    return this._managedCompanies;
  }

  public get generalData() {
    return this._generalData;
  }

  public get contactData() {
    return this._contactData;
  }

  public get locations() {
    return this._locations;
  }

  public get departments() {
    return this._departments;
  }

  public get costCenters() {
    return this._costCenters;
  }

  public get groups() {
    return this._groups;
  }

  public get paymentData() {
    return this._paymentData;
  }

  public get usesGocardlessDirectDebit() {
    return (
      Boolean(this._paymentData?.gocardless_customer_id) && !this._paymentData?.bank_transfer_only
    );
  }

  public get benefitSettings() {
    return this._benefitSettings;
  }

  public get settings() {
    return this._settings;
  }

  public get payrollExportMapping() {
    return this._payrollExportMapping;
  }

  public get customBenefitSettings() {
    return this._customBenefitSettings;
  }

  public get bookedBenefits() {
    return this._bookedBenefits;
  }

  public get benefitSets() {
    return this._benefitSets;
  }

  public get paychexApiCredentials() {
    return this._paychexApiCredentials;
  }

  public featureEnabled(flagName: string) {
    return (
      this._settings?.feature_flags &&
      flagName in this._settings.feature_flags &&
      this._settings.feature_flags[flagName] === true
    );
  }

  public async createLocation(data: LocationRequest) {
    if (!this.company) return;
    const res = await this.apiClient.locations.companiesLocationsCreate({
      parentCompany: this.company.id,
      requestBody: data
    });

    runInAction(() => {
      this._locations?.push(res);
    });
  }

  public async createDepartment(data: DepartmentRequest) {
    if (!this.company) return;
    const res = await this.apiClient.departments.companiesDepartmentsCreate({
      parentCompany: this.company.id,
      requestBody: data
    });

    runInAction(() => {
      this._departments?.push(res);
    });
  }

  public async createCostCenter(data: CostCenterRequest) {
    if (!this.company) return;
    const res = await this.apiClient.costCenters.companiesCostCentersCreate({
      parentCompany: this.company.id,
      requestBody: data
    });

    runInAction(() => {
      this._costCenters?.push(res);
    });
  }

  public async createGroup(data: GroupRequest) {
    if (!this.company) return;
    const res = await this.apiClient.groups.companiesGroupsCreate({
      parentCompany: this.company.id,
      requestBody: data
    });

    runInAction(() => {
      this._groups?.push(res);
    });
  }

  public async updateGeneralData(data: PatchedCompanyGeneralDataRequest) {
    if (!this.company) return;
    const res = await this.apiClient.companies.companiesGeneralDataPartialUpdate({
      id: this.company.id,
      requestBody: data
    });

    runInAction(() => (this._generalData = res));
  }

  public async updateContactData(data: PatchedCompanyContactDataRequest) {
    if (!this.company) return;
    const res = await this.apiClient.companies.companiesContactDataPartialUpdate({
      id: this.company.id,
      requestBody: data
    });

    runInAction(() => (this._contactData = res));
  }

  public async updateLocationData(id: string, data: PatchedLocationRequest) {
    if (!this.company) return;
    const res = await this.apiClient.locations.companiesLocationsPartialUpdate({
      id,
      parentCompany: this.company.id,
      requestBody: data
    });

    runInAction(() => {
      this._locations = this._locations?.map((l) => (l.id === id ? res : l));
    });
  }

  public async updateDepartmentData(id: string, data: PatchedDepartmentRequest) {
    if (!this.company) return;
    const res = await this.apiClient.departments.companiesDepartmentsPartialUpdate({
      id,
      parentCompany: this.company.id,
      requestBody: data
    });

    runInAction(() => {
      this._departments = this._departments?.map((department) =>
        department.id === id ? res : department
      );
    });
  }

  public async updateCostCenterData(id: string, data: PatchedCostCenterRequest) {
    if (!this.company) return;
    const res = await this.apiClient.costCenters.companiesCostCentersPartialUpdate({
      id,
      parentCompany: this.company.id,
      requestBody: data
    });

    runInAction(() => {
      this._costCenters = this._costCenters?.map((costCenter) =>
        costCenter.id === id ? res : costCenter
      );
    });
  }

  public async updateGroupData(id: string, data: PatchedGroupRequest) {
    if (!this.company) return;
    const res = await this.apiClient.groups.companiesGroupsPartialUpdate({
      id,
      parentCompany: this.company.id,
      requestBody: data
    });

    runInAction(() => {
      this._groups = this._groups?.map((group) => (group.id === id ? res : group));
    });
  }
  public async updatePaymentData(data: PatchedCompanyPaymentDataRequest) {
    if (!this.company) return;
    const res = await this.apiClient.companies.companiesPaymentDataPartialUpdate({
      id: this.company.id,
      requestBody: data
    });

    runInAction(() => {
      this._paymentData = res;
    });
  }

  public async updateSettings(data: PatchedCompanySettingsRequest) {
    if (!this.company) return;
    const res = await this.apiClient.companies.companiesSettingsPartialUpdate({
      id: this.company.id,
      requestBody: data
    });
    runInAction(() => {
      this._settings = res;
    });
  }

  public async updateJobbikeSettings(data: PatchedCompanyJobbikeSettingsPatchRequest) {
    return this.apiClient.companies.companiesBenefitSettingsJobbikePartialUpdate({
      id: this.company?.id ?? '',
      requestBody: data
    });
  }

  public async deleteLocation(id: string) {
    if (!this.company) return;
    await this.apiClient.locations.companiesLocationsDestroy({
      id,
      parentCompany: this.company.id
    });

    runInAction(() => {
      this._locations = this._locations?.filter((location) => location.id !== id);
    });
  }

  public async deleteDepartment(id: string) {
    if (!this.company) return;
    await this.apiClient.departments.companiesDepartmentsDestroy({
      id,
      parentCompany: this.company.id
    });

    runInAction(() => {
      this._departments = this._departments?.filter((department) => department.id !== id);
    });
  }

  public async deleteCostCenter(id: string) {
    if (!this.company) return;
    await this.apiClient.costCenters.companiesCostCentersDestroy({
      id,
      parentCompany: this.company.id
    });

    runInAction(() => {
      this._costCenters = this._costCenters?.filter((costCenter) => costCenter.id !== id);
    });
  }

  public async deleteGroup(id: string) {
    if (!this.company) return;
    await this.apiClient.groups.companiesGroupsDestroy({
      id,
      parentCompany: this.company.id
    });

    runInAction(() => {
      this._groups = this._groups?.filter((group) => group.id !== id);
    });
  }

  public async fetchPayrollSettings() {
    if (!this.company) {
      return;
    }
    const result = await this.apiClient.companies.companiesSettingsPayrollRetrieve({
      id: this.company.id
    });

    runInAction(() => {
      this._payrollExportMapping = Util.getKeys(result).map((key) => ({
        salaryName: key,
        externalId: result[key] ?? ''
      }));
    });
  }

  public async updatePayrollSettings(data: PayrollExportMappingRequest) {
    if (!this.company) return;

    const result = await this.apiClient.companies.companiesSettingsPayrollPartialUpdate({
      id: this.company.id,
      requestBody: data
    });

    runInAction(() => {
      this._payrollExportMapping = Util.getKeys(result).map((key) => ({
        salaryName: key,
        externalId: result[key] ?? ''
      }));
    });
  }

  public async isDatevConfigured() {
    if (!this._settings) {
      await this.fetchCompanySettings();
    }
    if (!this._payrollExportMapping) {
      await this.fetchPayrollSettings();
    }

    return !!this._settings?.datev_client_number && !!this._settings?.datev_consultant_number;
  }

  public async isMedianConfigured() {
    if (!this._settings) {
      await this.fetchCompanySettings();
    }
    return this._settings?.payroll_system === 'MEDIAN';
  }

  public async setCurrentCompanyById(companyId: string) {
    const companyRecord = this.managedCompanies?.find((c) => c.id === companyId);
    if (companyRecord) {
      const company = await this.apiClient.companies.companiesRetrieve({ id: companyId });
      await this.setCurrentCompany(company);
    }
  }

  public async setCurrentCompany(company: CompanyExtended) {
    if (!this._managedCompanies?.some((c) => c.id === company.id)) {
      throw Error('Company is not in the list of managed companies');
    }

    localStorage.setItem(CURRENT_COMPANY_STORAGE_KEY, company.id);

    runInAction(() => {
      this._company = company;
      this.fetchCompanySettings();
    });
  }

  public async getBenefitSettings(benefit: keyof CompanyBenefitSettings) {
    if (!this._benefitSettings) {
      await this.fetchBenefitSettings();
    }

    return this._benefitSettings && typeof this._benefitSettings != 'boolean'
      ? (this._benefitSettings[benefit] as CompanyBenefitSettings[keyof Omit<
          CompanyBenefitSettings,
          'custom'
        >])
      : undefined;
  }

  public async connectKombo() {
    if (!this._company) await this._fetchCompany();
    const company = this._company?.id;
    if (!company) {
      throw Error('No company');
    }
    const link = await this.getKomboLink(company);
    if (!link) {
      throw Error('Could not retrieve kombo connect link');
    }
    const token = await showKomboConnect(link);
    return token;
  }

  public async getKomboLink(company: string) {
    try {
      const result = await this.apiClient.companies.companiesHrisCreateKomboLinkCreate({
        parentCompany: company
      });
      if (result.status === 'success') {
        return result.data.link;
      }
    } catch (e) {
      console.error(e);
    }
  }

  public async getHrisSyncState() {
    if (!this.settings) {
      await this._fetchSettings();
    }
    if (
      !this._company?.id ||
      !this.settings?.hris_integration_status ||
      ['NONE', 'PAUSED'].includes(this.settings?.hris_integration_status)
    ) {
      this._hrisSyncState = null;
      this._hrisSyncDate = null;
      return;
    }

    this._hrisSyncState = (this.settings?.hris_last_sync_state as string) ?? null;
    this._hrisSyncDate = this.settings?.hris_last_sync_at ?? null;
  }

  public getHrisSyncStateValue(): string | null {
    return this._hrisSyncState;
  }

  public getHrisSyncDateValue(): string | null {
    return this._hrisSyncDate;
  }

  public async forceSync() {
    if (!this._company) await this._fetchCompany();
    if (this._company) {
      const company = this._company?.id;
      try {
        await this.apiClient.companies.companiesHrisForceHrisSyncCreate({
          parentCompany: company
        });
      } catch (e) {
        console.error(e);
      }
    }
  }

  public async deleteHrisIntegration() {
    if (!this._company) await this._fetchCompany();
    if (this._company) {
      const company = this._company?.id;
      try {
        await this.apiClient.companies.companiesHrisDeleteHrisIntegrationDestroy({
          parentCompany: company
        });
      } catch (e) {
        console.error(e);
      }
    }
  }

  public async getCustomBenefitSettings() {
    if (!this._customBenefitSettings) await this.fetchCustomBenefitSettings();
    return this._customBenefitSettings;
  }

  public async createCustomBenefit(request: CompanyCustomBenefitRequest) {
    if (!this._company) throw Error('No company selected');
    await this.apiClient.benefits.benefitsCustomCreate({
      company: this._company.id,
      requestBody: {
        ...request
      }
    });
  }

  public async patchCustomBenefit(request: CompanyCustomBenefitPatchRequest, benefitId: string) {
    if (!this._company) throw Error('No company selected');
    await this.apiClient.benefits.benefitsCustomPartialUpdate({
      id: benefitId,
      requestBody: {
        ...request
      }
    });
  }

  public async fetchCustomBenefit(benefitId: string) {
    if (!this.company) return;
    return this.apiClient.benefits.benefitsCustomRetrieve({
      id: benefitId
    });
  }
  public async fetchCustomBenefitAssignments(
    benefitId: string,
    startAccountingPeriodMonth?: number,
    startAccountingPeriodYear?: number
  ) {
    if (!this.company) return;

    return this.apiClient.benefits.benefitsCustomEmployeeAssignmentsList({
      benefitId,
      ...(startAccountingPeriodMonth !== undefined && { startAccountingPeriodMonth }),
      ...(startAccountingPeriodYear !== undefined && { startAccountingPeriodYear })
    });
  }
  public async openInvoicePortal() {
    if (!this.company) return;
    const url = await this.apiClient.payment.paymentInvoicesBillingLinkRetrieve({
      company: this.company.id
    });

    window.open(url, '_blank')?.focus();
  }

  public async fetchPaychexApiCredentials() {
    if (!this.company) return;
    const res = await this.apiClient.integrations.companiesPaychexCredentialsRetrieve({
      parentCompany: this.company.id
    });
    runInAction(() => (this._paychexApiCredentials = res));
  }

  public async fetchBookedBenefits() {
    if (!this.company) return;
    const res = await this.apiClient.companies.companiesPaymentDataRetrieve({
      id: this.company.id
    });
    runInAction(() => {
      this._bookedBenefits = res.booked_benefits?.sort((a, b) => a.localeCompare(b)) || [];
    });
  }
}

export default CompanyStore;
